<script setup lang="ts">
import type { SearchResult } from '../../types/search'

interface Props {
  results: SearchResult[]
  totalCount: number
  isLoading: boolean
  isLoadingMore?: boolean
  allResultsLoaded?: boolean
  error?: string
  selectedVersion?: string
  searchQuery?: string
  isRegex?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  selectedVersion: '',
  searchQuery: '',
  isRegex: false,
  isLoadingMore: false,
  allResultsLoaded: false,
})

const emit = defineEmits<{
  'result-select': [result: SearchResult]
  'load-more': []
}>()

const handleResultClick = (result: SearchResult): void => {
  emit('result-select', result)
}

const handleScroll = (event: Event): void => {
  const container = event.target as HTMLElement
  const scrollTop = container.scrollTop
  const scrollHeight = container.scrollHeight
  const clientHeight = container.clientHeight

  // 當捲軸到達底部時觸發載入
  if (scrollTop + clientHeight >= scrollHeight - 50) {
    emit('load-more')
  }
}

const highlightText = (text: string): string => {
  if (!text || !props.searchQuery.trim()) {
    return text
  }

  try {
    if (props.isRegex) {
      // 正則表達式高亮
      const regex = new RegExp(`(${props.searchQuery})`, 'gi')
      return text.replace(regex, '<mark>$1</mark>')
    } else {
      // 普通字串高亮（不分大小寫）
      const regex = new RegExp(`(${props.searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
      return text.replace(regex, '<mark>$1</mark>')
    }
  } catch {
    // 若高亮失敗，返回原文本
    return text
  }
}
</script>

<template>
  <div class="keyword-search-results">
    <!-- 結果統計 -->
    <div v-if="!isLoading && !error && results.length > 0" class="results-info">
      已加載 {{ results.length }} 筆結果
      <span v-if="!allResultsLoaded" class="status-hint">（繼續捲軸加載更多）</span>
    </div>

    <!-- Loading 狀態 -->
    <div v-if="isLoading" class="results-state">
      <div class="loading-spinner" />
      <span>搜尋中...</span>
    </div>

    <!-- Error 狀態 -->
    <div v-else-if="error" class="results-state error">
      <span>搜尋失敗：{{ error }}</span>
    </div>

    <!-- Empty 狀態 -->
    <div v-else-if="totalCount === 0" class="results-state">
      <span>未找到符合條件的經文</span>
    </div>

    <!-- 結果表格 -->
    <div v-else class="results-table-container" @scroll="handleScroll">
      <table class="results-table">
        <thead>
          <tr>
            <th class="col-number">編號</th>
            <th class="col-book">經卷</th>
            <th class="col-text">經文</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(result, index) in results"
            :key="`${result.book || result.bookName}-${result.chapter}-${result.verse}`"
            class="result-row"
            @click="handleResultClick(result)"
          >
            <td class="col-number">{{ index + 1 }}</td>
            <td class="col-book">{{ result.book || result.bookName }} {{ result.chapter }}:{{ result.verse }}</td>
            <td class="col-text" v-html="highlightText(result.text)"></td>
          </tr>
        </tbody>
      </table>

      <!-- 載入狀態 -->
      <div v-if="isLoadingMore" class="loading-more">
        <div class="loading-spinner-small" />
        <span>載入中...</span>
      </div>

      <!-- 全部載入完成 -->
      <div v-if="allResultsLoaded && !isLoadingMore && results.length > 0" class="load-complete">
        <span>已載入全部 {{ results.length }} 筆結果 ✓</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.keyword-search-results {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background-color: white;
}

.results-info {
  padding: 12px 16px;
  font-size: calc(var(--font-size-base) * 0.8);
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 500;
}

.results-info .status-hint {
  color: #9ca3af;
  font-size: calc(var(--font-size-base) * 0.7);
  font-weight: normal;
  margin-left: 6px;
}

.results-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 8px;
  color: #9ca3af;
  font-size: var(--font-size-base);
}

.results-state.error {
  color: #ef4444;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.results-table-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  border-top: 1px solid #e5e7eb;
}

.results-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-base);
}

.results-table thead {
  position: sticky;
  top: 0;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.results-table th {
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  color: #4b5563;
  height: 32px;
}

.col-number {
  width: 60px;
  text-align: center;
}

.col-book {
  width: 150px;
  min-width: 150px;
}

.col-text {
  flex: 1;
}

.result-row {
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.15s;
  cursor: pointer;
}

.result-row:hover {
  background-color: #f9fafb;
}

.result-row td {
  padding: 8px 12px;
  color: #1f2937;
  height: auto;
  vertical-align: top;
  word-wrap: break-word;
  white-space: normal;
}

.result-row .col-number {
  text-align: center;
  color: #9ca3af;
}

.result-row .col-book {
  font-weight: 500;
  color: #2563eb;
}

.result-row .col-text {
  color: #4b5563;
  line-height: 1.5;
}

mark {
  background-color: #ffeb3b;
  color: #333;
  padding: 0 2px;
  border-radius: 2px;
}

.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  color: #6b7280;
  font-size: calc(var(--font-size-base) * 0.8);
  border-top: 1px solid #e5e7eb;
}

.loading-spinner-small {
  width: 12px;
  height: 12px;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.load-complete {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: #9ca3af;
  font-size: calc(var(--font-size-base) * 0.8);
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}
</style>
